/**
 * Utility functions for route detection and pathname handling
 */

import { delocalizeRoute } from '../constants/url-constants';

/**
 * List of all tools page routes. Includes the retired list pages (kept recognized so a
 * bookmarked old URL still gets the tools-page chrome/layout treatment while its redirect
 * fires) alongside the unified `/tools` hub that replaced them.
 */
export const TOOLS_ROUTES = [
  '/tools',
  '/tools/searches',
  '/tools/watchlist',
  '/tools/job-flow-sankey',
  '/tools/viewed-jobs',
  '/tools/saved-jobs',
  '/tools/application-kits',
  '/tools/applied-jobs',
  '/tools/interviewing-jobs',
  '/tools/offer-jobs',
  '/tools/rejected-jobs',
  '/tools/hired-jobs',
  '/tools/hidden-jobs',
  '/tools/saved-searches',
  '/tools/job-alerts/matches',
] as const;

export interface HubNavItem {
  route: string;
  labelId: string;
  defaultLabel: string;
}

/**
 * Canonical seeker-hub navigation registry — the single source of truth for the persistent
 * header "My Jobs" entry, the mobile menu, and the hub's own tab row. Replaces the three
 * previously-duplicated tool lists (ToolSwitcher.tsx, NavigationNavbar.tsx, TOOLS_ROUTES
 * consumers). Extended in later phases as Searches & Alerts / Watchlist become real surfaces
 * of their own.
 */
export const HUB_NAV_ITEMS: HubNavItem[] = [
  { route: '/tools', labelId: 'hub.nav.myJobs', defaultLabel: 'My Jobs' },
  { route: '/tools/watchlist', labelId: 'hub.nav.watchlist', defaultLabel: 'Watchlist' },
  { route: '/tools/searches', labelId: 'hub.nav.searchesAlerts', defaultLabel: 'Searches & Alerts' },
  { route: '/profile', labelId: 'hub.nav.profile', defaultLabel: 'Profile & CV' },
];

function stripLeadingLocale(pathname: string): { path: string; localeHint: 'en' | 'fi' | 'sv' | null } {
  const clean = pathname.split('?')[0];
  const segments = clean.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase();
  if (first === 'en' || first === 'fi' || first === 'sv') {
    const rest = segments.slice(1).join('/');
    return { path: rest ? `/${rest}` : '/', localeHint: first };
  }
  return { path: clean.startsWith('/') ? clean : `/${clean}`, localeHint: null };
}

/**
 * Canonical English tools path (e.g. `/tools/saved-jobs`) for any localized or legacy tools URL.
 */
export function englishCanonicalToolsPathFromPathname(pathname: string): string | null {
  const { path, localeHint } = stripLeadingLocale(pathname);
  const normalized = path.length > 1 ? path.replace(/\/+$/, '') : path;

  for (const route of TOOLS_ROUTES) {
    if (normalized === route) return route;
  }

  const localesToTry: Array<'en' | 'fi' | 'sv'> = localeHint ? [localeHint] : ['en', 'fi', 'sv'];
  for (const loc of localesToTry) {
    const english = delocalizeRoute(normalized, loc).replace(/\/+$/, '');
    for (const route of TOOLS_ROUTES) {
      if (english === route) return route;
    }
  }
  return null;
}

/**
 * Checks if a given pathname is a tools page route
 * Handles both localized and non-localized paths
 */
export function isToolsPage(pathname: string): boolean {
  return englishCanonicalToolsPathFromPathname(pathname) != null;
}

/**
 * Gets the current tool route from a pathname
 * Returns the normalized route (without locale) or null if not a tools page
 */
export function getCurrentToolRoute(pathname: string): string | null {
  return englishCanonicalToolsPathFromPathname(pathname);
}

/**
 * Gets the viewMode for tools pages, normalizing 'map' to 'list' since map view
 * is not available on tools pages.
 * @param defaultMode - The default mode to use if no stored value or invalid value
 * @returns 'grid' | 'list' (never 'map')
 */
export function getToolsPageViewMode(defaultMode: 'grid' | 'list' = 'list'): 'grid' | 'list' {
  if (typeof window === 'undefined') return defaultMode;

  // Check both 'layout' and 'jobcrawls.viewMode' for backward compatibility
  const stored = localStorage.getItem('layout') || localStorage.getItem('jobcrawls.viewMode');

  // If stored value is 'map', normalize to 'list' since map view is not available on tools pages
  if (stored === 'map') {
    // Update localStorage to reflect the normalized value
    try {
      localStorage.setItem('layout', 'list');
      localStorage.setItem('jobcrawls.viewMode', 'list');
    } catch (err) {
      console.warn('[route-utils] Failed to update viewMode in localStorage:', err);
    }
    return 'list';
  }

  // Return stored value if it's valid for tools pages, otherwise return default
  if (stored === 'list' || stored === 'grid') {
    return stored as 'grid' | 'list';
  }

  return defaultMode;
}
